import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from "firebase/functions";
import { db, functions, auth, tDoc, tSubCollection } from '../../../firebase';
import { TENANT_ID } from "../../../tenant";

import {
  savePublicShopCart,
  loadPublicShopCart,
  setResumeShopAfterAuth,
  clearPublicShopCart,
  saveCheckoutIntent,
  loadCheckoutIntent,
  clearCheckoutIntent,
} from "../../../lib/publicShopSession";

import { collection, addDoc, onSnapshot, setDoc, doc, getDoc, query, where, orderBy, limit } from 'firebase/firestore';
import {
  X,
  Ticket,
  Crown,
  Loader2,
  ShoppingCart,
  Info,
  Minus,
  Plus,
  Trash2,
  ArrowLeft,
  Handbag,
} from 'lucide-react';
import ShopModalMessage from "./ShopModalMessage";

const Loader = ({ size = 24, className = '' }) => (
  <div className={`flex justify-center items-center ${className}`}>
    <Loader2 size={size} className="animate-spin text-gray-900" />
  </div>
);

const TAB_CONFIG = {
  ticket: { label: 'eチケット', icon: Ticket, color: 'text-gray-900', btnBg: 'bg-accent' },
  goods: { label: 'グッズ', icon: Handbag, color: 'text-gray-900', btnBg: 'bg-accent' },
  plan: { label: '月額プラン', icon: Crown, color: 'text-gray-900', btnBg: 'bg-accent' },
};

// カートは goods と ticket を跨いで共通利用
const CART_TABS = new Set(['ticket', 'goods']);

const resolveStripePriceId = (p) =>
  (typeof p?.active_price_id === "string" && p.active_price_id) ||
  (typeof p?.default_price === "string" && p.default_price) ||
  (typeof p?.price?.id === "string" && p.price.id) ||
  null;

const ShopModalLocal = ({
  isOpen,
  onClose,
  preloadedProductsByType = { ticket: [], goods: [], plan: [] },
  preloadedReady = false,
  userProfile = null,
  profileSourceReady = false,
  publicView = false,
  publicGender = "",
}) => {
    const navigate = useNavigate();

  const goToAuthForShop = (intent = null) => {
    savePublicShopCart(cart);
    setResumeShopAfterAuth(true);

    if (intent) {
      saveCheckoutIntent(intent);
    } else {
      clearCheckoutIntent();
    }

    onClose?.();
    navigate("/auth/start", {
      state: {
        redirectTo: "/",
      },
    });
  };
    // --- system/app_config (terms_url) ---
  const [termsUrl, setTermsUrl] = useState('');

  const alreadyAgreedToTerms = Boolean(
    userProfile?.consents?.terms_agreed_at
  );

  const requireTermsConsent =
    Boolean(String(termsUrl || '').trim()) && !alreadyAgreedToTerms;

    // ----- view control -----
    // 'shop' | 'profile'
    const [view, setView] = useState('shop');
    // pendingAction: { type: 'ADD_TO_CART', product } | { type:'CHECKOUT_PLAN', priceId } | { type:'CHECKOUT_CART' }
    const [pendingAction, setPendingAction] = useState(null);

    // Hooks
    const [activeTab, setActiveTab] = useState('ticket');

    const listRef = useRef(null);

    // purchasing:
    // - plan の単体 checkout 中：priceId
    // - cart checkout 中：'__cart__'
    // - profile saving 中：'__profile__'
    const [purchasing, setPurchasing] = useState(null);

const formatDateTimeJP = (ms) => {
  if (!ms || !Number.isFinite(Number(ms))) return '';
  try {
    // 秒が混ざっててもmsに寄せる
    const t = Number(ms);
    const d = new Date(t < 1e12 ? t * 1000 : t);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yy}/${mm}/${dd} ${hh}:${mi}`;
  } catch {
    return '';
  }
};

// Stripe/Firestore由来の "current_period_end" を色々な形から ms へ
const pickEffectivePeriodEndMs = (data, fallbackSub) => {
  const v =
    data?.currentPeriodEndMs ??
    data?.current_period_end_ms ??
    data?.currentPeriodEnd ??
    data?.current_period_end ??
    null;

  const ms = toMs(v);
  if (ms) return ms;

  // フォールバック：今見えている activeSub（既存ロジック）
  const subMs = getSubEndMs(fallbackSub);
  return subMs || null;
};

const ProductTitle = ({ name }) => {
  const ref = useRef(null);
  const [displayText, setDisplayText] = useState(name);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const lineHeight = parseFloat(
      window.getComputedStyle(el).lineHeight
    );

    const lines = Math.round(el.clientHeight / lineHeight);

    if (lines > 1 && name.includes("｜")) {
      setDisplayText(name.split("｜").join("\n"));
    } else {
      setDisplayText(name);
    }
  }, [name]);

  return (
    <h3
      ref={ref}
      className="font-black text-sm mb-1 leading-snug whitespace-pre-line break-words"
    >
      {displayText}
    </h3>
  );
};

    // --- plan change ---
// --- plan change (which price is being changed) ---
  const [changingPlanPriceId, setChangingPlanPriceId] = useState(null); // string | null  // --- plan membership (source of truth: entitlements/plan_active) ---
  // --- plan membership (source of truth: entitlements/plan_active) ---
  const [planEntitlement, setPlanEntitlement] = useState(null);
  const [planEntitlementReady, setPlanEntitlementReady] = useState(false);

  const [activeSub, setActiveSub] = useState(null);
  const [activeSubReady, setActiveSubReady] = useState(false);

  const getSubItemsArray = (s) => {
  if (!s) return [];
  if (Array.isArray(s.items)) return s.items;                 // 想定A
  if (Array.isArray(s.items?.data)) return s.items.data;      // Stripe extension 想定B
  if (Array.isArray(s.items?.items)) return s.items.items;    // 念のため
  return [];
};

const getSubItem0 = (s) => getSubItemsArray(s)[0] || null;

const getSubPriceId = (s) => {
  const it0 = getSubItem0(s);
  return String(
    s?.stripe_price_id ||
    s?.price_id ||
    s?.priceId ||
    it0?.price?.id ||
    it0?.plan?.id ||
    ""
  ).trim();
};

const getSubEndMs = (s) => {
  const it0 = getSubItem0(s);
  return (
    toMs(s?.current_period_end) ||
    toMs(s?.currentPeriodEnd) ||
    toMs(s?.expires_at) ||
    toMs(s?.expiresAt) ||
    toMs(s?.valid_until) ||
    toMs(s?.validUntil) ||
    toMs(s?.end_at) ||
    toMs(s?.endAt) ||
    toMs(it0?.current_period_end) ||
    toMs(it0?.currentPeriodEnd) ||
    null
  );
};

const getSubStatus = (s) => {
  const it0 = getSubItem0(s);
  const raw =
    s?.status ||
    s?.subscription_status ||
    s?.subscriptionStatus ||
    s?.stripe_status ||
    s?.stripeStatus ||
    it0?.status ||
    "";
  return String(raw).toLowerCase().trim();
};

const getSubId = (s) => {
  return String(
    s?.stripe_subscription_id ||
    s?.subscription_id ||
    s?.subscriptionId ||
    s?.id || // docIdを詰めたやつ
    ""
  ).trim();
};

const toMs = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === "function") {
    try {
      const ms = v.toDate().getTime();
      return Number.isFinite(ms) ? ms : null;
    } catch {
      return null;
    }
  }
  if (typeof v === "object" && v?.seconds != null) {
    const ms = Number(v.seconds) * 1000 + Math.floor(Number(v.nanoseconds || 0) / 1e6);
    return Number.isFinite(ms) ? ms : null;
  }
  if (v instanceof Date) {
    const ms = v.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof v === "number") {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return n < 1e12 ? n * 1000 : n; // 秒→ms
  }
  if (typeof v === "string") {
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
};

useEffect(() => {
  if (!isOpen) return;

  setActiveSubReady(false);

  const authUid = auth.currentUser?.uid;
  const customerUid =
    String(userProfile?.customerUid || userProfile?.customer_uid || "").trim() || authUid;

  if (!customerUid) {
    setActiveSub(null);
    setActiveSubReady(true);
    return;
  }
  const customerRef = tDoc('customers', customerUid, TENANT_ID);
  // ✅ (1) tenants配下 と (2) root配下 の両方を監視して、どちらか取れた方を採用
  const subsColTenant = tSubCollection("customers", customerUid, "subscriptions", TENANT_ID);

const pickActive = (docs) => {
  console.log("[TENANT_ID]", TENANT_ID, "sub.tenant_id", docs?.[0]?.data?.()?.tenant_id);
  const now = Date.now();
  const candidates = (docs || [])
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => {
      const tid = String(s?.tenant_id || "");
      if (!tid) return true;
      return tid === String(TENANT_ID).trim(); // まずはこのまま
    })
    .filter((s) => ["active", "trialing", "past_due"].includes(getSubStatus(s)))
/*
    .filter((s) => {
      const endMs = getSubEndMs(s);
      // ✅ endMs が無い場合は「一旦OK」（データ欠損対策）
      return !endMs || endMs > now;
    });
*/
  // ✅ endMsが取れるものを優先して並べる（取れないものは最後）
  candidates.sort((a, b) => {
    const ea = getSubEndMs(a);
    const eb = getSubEndMs(b);
    const ka = ea ? 1 : 0;
    const kb = eb ? 1 : 0;
    if (ka !== kb) return kb - ka;
    return (eb || 0) - (ea || 0);
  });

  return candidates[0] || null;
};  
  let bestFromTenant = null;
  let readyTenant = false;

  const commit = () => {
    const chosen = bestFromTenant || null;
    setActiveSub(chosen);
    setActiveSubReady(readyTenant);
  };

  const unsubTenant = onSnapshot(
    subsColTenant,
    (snap) => {
      bestFromTenant = pickActive(snap.docs);
      readyTenant = true;
      commit();
    },
    (e) => {
      console.warn("[ShopModalLocal] subscriptions(TENANT) snapshot error", e?.code, e?.message);
      bestFromTenant = null;
      readyTenant = true;
      commit();
    }
  );


  return () => {
    try { unsubTenant?.(); } catch {}
  };
}, [isOpen, TENANT_ID, userProfile?.customerUid, userProfile?.customer_uid]);

  useEffect(() => {
    if (!isOpen) return;

    setPlanEntitlementReady(false);

    const authUid = auth.currentUser?.uid;
    const customerUid =
      String(userProfile?.customerUid || userProfile?.customer_uid || "").trim() || authUid;

    if (!customerUid) {
      setPlanEntitlement(null);
      setPlanEntitlementReady(true);
      return;
    }

    // ★ entitlements コレクションから「有効な plan」を拾う
    const entCol = tSubCollection("customers", customerUid, "entitlements", TENANT_ID);

    // ⚠️ ここはあなたの実データのフィールド名に合わせてください
    // よくある候補:
    // - type == "plan"
    // - purchase_category == "plan"
    // - entitlement_type == "plan"
    // ★ entitlements コレクションから「plan_active」を直で購読する（A方針の正本）
    const entDocRef = doc(
      tSubCollection("customers", customerUid, "entitlements", TENANT_ID),
      "plan_active"
    );

    const unsub = onSnapshot(
      entDocRef,
      (snap) => {
        const d = snap.exists() ? snap.data() : null;
        // plan_active が無い場合は null（= subscriptions にフォールバック）
        setPlanEntitlement(d ? ({ id: snap.id, ...d }) : null);
        setPlanEntitlementReady(true);

        // ✅ デバッグ（必要なら残す）
        console.log("[ShopModalLocal] plan_active snap", {
          exists: snap.exists(),
          id: snap.id,
          stripe_price_id: d?.stripe_price_id,
          product_name: d?.product_name,
          door_ids: d?.door_ids,
          is_active: d?.is_active,
        });
      },
      (e) => {
        console.warn("[ShopModalLocal] plan_active snapshot error", e?.code, e?.message, e);
        setPlanEntitlement(null);
        setPlanEntitlementReady(true);
      }
    );
    return () => {
      try { unsub?.(); } catch {}
    };
  }, [isOpen, TENANT_ID, userProfile?.customerUid, userProfile?.customer_uid]);
const effectiveReady = planEntitlementReady && activeSubReady;
const effectivePlan = planEntitlement || activeSub;

// ★現在プランの priceId（plan_active 優先、無ければ subscriptions）
const currentPlanPriceId = useMemo(() => {
  // 1) plan_active（entitlements）の stripe_price_id を最優先
  const fromPlanActive = String(
    planEntitlement?.stripe_price_id ||
    planEntitlement?.price_id ||
    planEntitlement?.priceId ||
    ""
  ).trim();
  if (fromPlanActive) return fromPlanActive;

  // 2) customers の active_plan_price_id（Functionsが書くなら強い）
  const fromCustomer = String(userProfile?.active_plan_price_id || "").trim();
  if (fromCustomer) return fromCustomer;

  // 3) 最後に subscriptions
  const fromSub = String(getSubPriceId(activeSub) || "").trim();
  return fromSub || "";
}, [planEntitlement, userProfile?.active_plan_price_id, activeSub]);

// ===== plan pricing map (for upgrade/downgrade判定) =====
const planAmountByPriceId = useMemo(() => {
  const m = new Map();
  const arr = Array.isArray(preloadedProductsByType?.plan) ? preloadedProductsByType.plan : [];
  for (const p of arr) {
    const pid = String(resolveStripePriceId(p) || "").trim();
    if (!pid) continue;
    const amt = Number(p?.unit_amount ?? p?.price?.unit_amount ?? 0) || 0;
    m.set(pid, amt);
  }
  return m;
}, [preloadedProductsByType?.plan]);

const getPlanAmount = (priceId) => {
  const pid = String(priceId || "").trim();
  return planAmountByPriceId.get(pid) ?? null;
};

// ===== pending_change (plan_active優先、なければ subscriptions から推定) =====
const pendingChange = useMemo(() => {
  // 1) plan_active（entitlements）に pending_change があるなら最優先
  const pc = planEntitlement?.pending_change || planEntitlement?.pendingChange || null;
  if (pc) {
    const effectiveAtMs =
      toMs(pc?.effective_at_ms) ||
      toMs(pc?.effectiveAtMs) ||
      toMs(pc?.effective_at) ||
      toMs(pc?.effectiveAt) ||
      null;

    const targetPriceId = String(pc?.target_price_id || pc?.targetPriceId || "").trim();
    const scheduleId = String(pc?.schedule_id || pc?.scheduleId || "").trim();

    return { ...pc, effectiveAtMs, targetPriceId, scheduleId };
  }

  // 2) なければ subscriptions から「ダウングレード予約」を推定
  //    あなたの実データ: schedule_id / target_price_id / change_type=downgrade が入っている
  const scheduleId = String(activeSub?.schedule_id || activeSub?.scheduleId || "").trim();
  const changeType = String(activeSub?.change_type || activeSub?.changeType || "").toLowerCase().trim();
  const targetPriceId = String(activeSub?.target_price_id || activeSub?.targetPriceId || "").trim();

  // schedule_id がある or change_type が downgrade なら「予約中」とみなす
  const looksPending = Boolean(scheduleId) || changeType === "downgrade";
  if (!looksPending || !targetPriceId) return null;

  // 反映タイミングは「次回請求期」= current_period_end を使うのが自然
  const effectiveAtMs =
    toMs(activeSub?.current_period_end) ||
    toMs(activeSub?.currentPeriodEnd) ||
    getSubEndMs(activeSub) ||
    null;

  return { effectiveAtMs, targetPriceId, scheduleId, source: "subscription_fallback" };
}, [planEntitlement, activeSub]);

// ✅ 超厳格：Stripeのsubscriptionが「有効」かつ current_period_end が未来のときだけ会員扱い
// （plan_active を優先しつつ、無ければ subscriptions を見る）
const hasActivePlan = useMemo(() => {
  if (!effectiveReady) return false;
  if (!effectivePlan) return false;

  // ✅ 1) entitlements 由来（planEntitlement）がある場合は、subscription前提の判定をしない
  //    （planEntitlement は構造が違うので getSubStatus/getSubId/getSubEndMs が当てにならない）
  if (planEntitlement) {
    // plan_active は is_active が正本
    if (planEntitlement?.is_active === false) return false;

    // 期限がある設計にしてるなら尊重（無ければ is_active=true でOK）
    const endMs =
      toMs(planEntitlement?.expires_at) ||
      toMs(planEntitlement?.expiresAt) ||
      toMs(planEntitlement?.valid_until) ||
      toMs(planEntitlement?.validUntil) ||
      toMs(planEntitlement?.end_at) ||
      toMs(planEntitlement?.endAt) ||
      null;

    if (!endMs) return true;
    return endMs > Date.now();
  }

  // ✅ 2) subscriptions 由来（従来ロジック）
  const status = getSubStatus(effectivePlan);
  if (status && !["active", "trialing", "past_due"].includes(status)) return false;

  const priceId = getSubPriceId(effectivePlan);
  if (!priceId) return false;

  const looksLikeSubscriptionDoc = Boolean(activeSub);
  if (looksLikeSubscriptionDoc) {
    const subId = getSubId(effectivePlan);
    if (!subId) return false;
  }

  const endMs = getSubEndMs(effectivePlan);

  // ✅ endMs が無い場合は「暫定で有効」扱い（データ欠損救済）
  if (!endMs) return true;

  return endMs > Date.now();
}, [effectiveReady, effectivePlan, activeSub, planEntitlement]);

const pendingChangeActive = useMemo(() => {
  if (!effectiveReady) return false;
  if (!hasActivePlan) return false;
  if (!pendingChange) return false;

  // schedule_id があるなら「予約中」
  if (pendingChange?.scheduleId) return true;

  // effectiveAtMs が未来なら「予約中」
  if (pendingChange?.effectiveAtMs) return pendingChange.effectiveAtMs > Date.now();

  return false;
}, [effectiveReady, hasActivePlan, pendingChange]);
/*
const pendingChangeLabel = useMemo(() => {
  if (!pendingChangeActive) return "";
  const ms = pendingChange.effectiveAtMs;
  const daysLeft = Math.max(0, Math.ceil((ms - Date.now()) / 86400000));
  const when = formatDateTimeJP(ms);
  const target = pendingChange?.targetPriceId ? `（予約先：${pendingChange.targetPriceId}）` : "";
  return `あと${daysLeft}日で反映${when ? ` / ${when} 以降` : ""} ${target}`.trim();
}, [pendingChangeActive, pendingChange]);
*/


const planNameByPriceId = useMemo(() => {
  const m = new Map();
  const arr = Array.isArray(preloadedProductsByType?.plan) ? preloadedProductsByType.plan : [];
  for (const p of arr) {
    const pid = String(resolveStripePriceId(p) || "").trim();
    if (!pid) continue;
    m.set(pid, String(p?.name || "").trim());
  }
  return m;
}, [preloadedProductsByType?.plan]);

const pendingTargetPlanName = useMemo(() => {
  const pid = String(pendingChange?.targetPriceId || "").trim();
  if (!pid) return "";
  return planNameByPriceId.get(pid) || "";
}, [pendingChange?.targetPriceId, planNameByPriceId]);

  // cart: { [priceId]: { qty, productId, name, description, unit_amount, priceId, type } }
  const [cart, setCart] = useState({});

  useEffect(() => {
    if (!isOpen) return;

    const restored = loadPublicShopCart();
    if (restored && typeof restored === "object" && Object.keys(restored).length > 0) {
      setCart(restored);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    savePublicShopCart(cart);
  }, [cart, isOpen]);

  // ===== price resolver (front) =====
  // goods/ticket の全プリロードから「productId -> 最新 priceId」を作る
  const latestPriceByProductId = useMemo(() => {
    const map = new Map();

    const pickLatestPriceId = (p) => {
      // 優先順：active_price_id / default_price / p.price.id
      const a = String(p?.active_price_id || '').trim();
      if (a) return a;
      const d = String(p?.default_price || '').trim();
      if (d) return d;

      const pid = String(p?.price?.id || '').trim();
      if (pid) return pid;

      return '';
    };

    const merge = (arr) => {
      (Array.isArray(arr) ? arr : []).forEach((p) => {
        if (!p?.id) return;
        const priceId = pickLatestPriceId(p);
        if (!priceId) return;
        map.set(String(p.id), priceId);
      });
    };

    // ticket/goods だけ（planは別ロジック）
    merge(preloadedProductsByType?.ticket);
    merge(preloadedProductsByType?.goods);

    return map;
  }, [preloadedProductsByType]);

  const resolveLatestPriceIdForProduct = (p) => {
    const productId = String(p?.id || '').trim();
    if (!productId) return '';

    const priceId = latestPriceByProductId.get(productId) || '';
    return String(priceId || '').trim();
  };

  // cart 内の priceId を「最新」に寄せる（商品IDベースで再解決）
  const normalizeCartToLatestPrices = () => {
    setCart((prev) => {
      const next = {};
      for (const [oldPriceId, it] of Object.entries(prev || {})) {
        const productId = String(it?.productId || '').trim();
        if (!productId) continue;

        const latest = String(latestPriceByProductId.get(productId) || '').trim();
        const usePriceId = latest || String(it?.priceId || oldPriceId || '').trim();

        if (!usePriceId) continue;

        // 同一priceIdに収束する場合は qty を合算
        const cur = next[usePriceId];
        const qty = Math.max(1, Number(it?.qty || 1) || 1);

        next[usePriceId] = {
          ...(cur || it),
          ...it,
          priceId: usePriceId,
          qty: (cur?.qty || 0) + qty,
        };
      }
      return next;
    });
  };

  const isCartTab = CART_TABS.has(activeTab);

  // ✅ 同意チェック
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // ✅ プリロードが更新されたら、カートの priceId を最新へ寄せる（体感のズレ防止）

  useEffect(() => {
    if (!isOpen) return;
    if (!preloadedReady) return;
    normalizeCartToLatestPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preloadedReady, latestPriceByProductId]);

  // ===== eligibility (attribute / gender) =====
  const normalizeAttr = (v) => {
    const s = String(v || '').trim();
    if (!s) return '';
    if (s.includes('法人')) return '法人';
    if (s.includes('個人')) return '個人';
    return s;
  };

  const normalizeGender = (v) => {
    const s = String(v || '').trim().toLowerCase();
    if (!s) return '';
    if (s === 'male' || s.includes('男性')) return 'male';
    if (s === 'female' || s.includes('女性')) return 'female';
    if (s === 'none') return 'none';
    return s; // 想定外はそのまま
  };

  const getUserAttr = (up) =>
    normalizeAttr(up?.attribute ?? up?.metadata?.attribute ?? '');

  const getUserGender = (up) =>
    normalizeGender(up?.gender ?? up?.metadata?.gender ?? '');

  const getProductTargetAttr = (p) =>
    normalizeAttr(p?.target_attribute ?? p?.metadata?.target_attribute ?? p?.metadata?.targetAttribute ?? '');

  const getProductGenderRestriction = (p) =>
    normalizeGender(p?.gender_restriction ?? p?.metadata?.gender_restriction ?? p?.metadata?.genderRestriction ?? '');

  const isProductAllowedForUser = (p, up) => {
    // user が未取得なら「一旦出す」(厳密にやると空になるので運用優先)
    if (!up) return true;

    const userAttr = getUserAttr(up);
    const userGender = getUserGender(up);

    const needAttr = getProductTargetAttr(p);              // '法人' | '個人' | ''
    const needGender = getProductGenderRestriction(p);     // 'male' | 'female' | 'none' | ''

    // attribute 制限
    if (needAttr && needAttr !== 'all') {
      if (!userAttr) return false;        // ユーザー属性が空なら弾く（要件次第で true にしても良い）
      if (userAttr !== needAttr) return false;
    }

    // gender 制限
    if (needGender && needGender !== 'none' && needGender !== 'all') {
      if (!userGender) return false;
      if (userGender !== needGender) return false;
    }

    return true;
  };

  // ✅ モーダルでは fetch せず、プリロードされたデータを表示
  const products = useMemo(() => {
    const list = preloadedProductsByType?.[activeTab] || [];
    const arr = Array.isArray(list) ? list : [];

    if (publicView) {
      const normalizedPublicGender = normalizeGender(publicGender);

      return arr.filter((p) => {
        const needGender = getProductGenderRestriction(p);

        // 制限なし商品は常に表示
        if (!needGender || needGender === "none" || needGender === "all") {
          return true;
        }

        // 仮性別未選択なら性別制限商品は出さない
        if (!normalizedPublicGender) {
          return false;
        }

        return needGender === normalizedPublicGender;
      });
    }

    return arr.filter((p) => isProductAllowedForUser(p, userProfile));
  }, [preloadedProductsByType, activeTab, userProfile, publicView, publicGender]);
/*
  useEffect(() => {
  console.log("[ShopModalLocal] activeTab =", activeTab);
  console.log("[ShopModalLocal] preloadedProductsByType =", preloadedProductsByType);
  console.log("[ShopModalLocal] products =", products);
}, [activeTab, preloadedProductsByType, products]);
*/
  const cartItems = useMemo(() => Object.values(cart), [cart]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, it) => sum + (it.qty || 0), 0),
    [cartItems]
  );

  const cartSubtotal = useMemo(
    () => cartItems.reduce((sum, it) => sum + (it.unit_amount || 0) * (it.qty || 0), 0),
    [cartItems]
  );

  // ---- profile draft ----
  // Firestoreスキーマ（スクショ）に寄せる：
  // - name: root（存在する実データがあるため読む/書く）
  // - email: root
  // - address: { postal_code, state, city, line1, line2, country }
  // - metadata: { name, birthday }
  const [profileDraft, setProfileDraft] = useState({
    name: '',
    email: '',
    gender: '',
    birthY: '',
    birthM: '',
    birthD: '',
    address: {
      postal_code: '',
      state: '',
      city: '',
      line1: '',
      line2: '',
      country: 'JP',
    },
  });

  const [profileDraftReady, setProfileDraftReady] = useState(false);

  const [profileError, setProfileError] = useState('');

  // Birth inputs: auto-advance
  const yRef = useRef(null);
  const mRef = useRef(null);
  const dRef = useRef(null);

  // タブ説明（system/category_config）
const [tabDescriptions, setTabDescriptions] = useState({ goods: '', ticket: '', plan: '' });

useEffect(() => {
  if (!isOpen) return;

    const ref = tDoc("config", "category_config", TENANT_ID);
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.data() || {};
      setTabDescriptions({
        goods: String(d.goods || ""),
        ticket: String(d.ticket || ""),
        plan: String(d.plan || ""),
      });
    }, (err) => {
      console.error("[DENIED] onSnapshot: tenants/{TENANT_ID}/config/category_config",
        "path=", ref.path, "code=", err?.code, "message=", err?.message
      );
    });

    return () => unsub();
  }, [isOpen, TENANT_ID]);

const tabDescriptionText = useMemo(() => {
  if (activeTab === 'goods') return tabDescriptions.goods;
  if (activeTab === 'ticket') return tabDescriptions.ticket;
  if (activeTab === 'plan') return tabDescriptions.plan;
  return '';
}, [activeTab, tabDescriptions]);

const tabDescStyle = useMemo(() => {
  if (activeTab === 'ticket') {
    return {
      box: 'bg-gray-50 border-gray-200',
      text: 'text-gray-900',
      icon: 'bg-gray-50 border-gray-900 text-gray-900',
    };
  }
  if (activeTab === 'goods') {
    return {
      box: 'bg-gray-50 border-gray-200',
      text: 'text-gray-900',
      icon: 'bg-gray-50 border-gray-900 text-gray-900',
    };
  }
  if (activeTab === 'plan') {
    return {
      box: 'bg-gray-50 border-gray-200',
      text: 'text-gray-900',
      icon: 'bg-gray-50 border-gray-900 text-gray-900',
    };
  }
  return {
    box: 'bg-gray-50 border-gray-100',
    text: 'text-gray-600',
    icon: 'bg-gray-50 border-gray-500 text-gray-500',
  };
}, [activeTab]);

  // ✅ terms_url を購読（isOpen の時だけ）
  useEffect(() => {
    if (!isOpen) return;

    const unsub = onSnapshot(tDoc("config", "app", TENANT_ID), (snap) => {
      const d = snap.data() || {};
      setTermsUrl(String(d?.terms_url || '').trim());
    });

    return () => unsub();
  }, [isOpen, TENANT_ID]);

  // 初期値（userProfile + auth.currentUser）を draft に流し込む
  useEffect(() => {
    if (!isOpen) return;

    const u = auth.currentUser;

    // ログイン済みの通常導線では、親のプロフィール読込完了を待つ
    if (!publicView && u && !profileSourceReady) {
      setProfileDraftReady(false);
      return;
    }

    setProfileDraftReady(false);

    const emailFromAuth = u?.email || '';

    const up = userProfile || {};
    const upMeta = up?.metadata || {};

    const nameFromDb = up?.name || upMeta?.name || '';
    const emailFromDb = up?.email || '';

    const birthday = upMeta?.birthday || '';
    const [by, bm, bd] = birthday ? String(birthday).split('-') : ['', '', ''];

    setProfileDraft((prev) => ({
      ...prev,
      name: nameFromDb || '',
      email: emailFromDb || emailFromAuth || '',
      gender: up?.gender || upMeta?.gender || '',
      birthY: by || '',
      birthM: bm || '',
      birthD: bd || '',
      address: {
        postal_code: up?.address?.postal_code || '',
        state: up?.address?.state || '',
        city: up?.address?.city || '',
        line1: up?.address?.line1 || '',
        line2: up?.address?.line2 || '',
        country: up?.address?.country || 'JP',
      },
    }));

    setProfileDraftReady(true);
  }, [isOpen, userProfile, publicView, profileSourceReady]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({
      top: 0,
      behavior: 'auto', // 'smooth' でもOK
    });
  }, [activeTab]);

  // モーダル close/open の掃除
  useEffect(() => {
    if (!isOpen) return;

    return () => {
      setPurchasing(null);
      setView('shop');
      setPendingAction(null);
      setProfileError('');
      setAgreedToTerms(false);
      setProfileDraftReady(false);
      resumedIntentRef.current = false;
    };
  }, [isOpen]);

  // ---------- helpers: profile check ----------
  const normalizeEmail = (v) => String(v || '').trim();
  const normalizeName = (v) => String(v || '').trim();
  const normalizeBirthPart = (v) => String(v || '').trim();

  const buildBirthday = () => {
    const y = normalizeBirthPart(profileDraft.birthY);
    const m = normalizeBirthPart(profileDraft.birthM);
    const d = normalizeBirthPart(profileDraft.birthD);
    if (!y && !m && !d) return '';
    const mm = m ? m.padStart(2, '0') : '';
    const dd = d ? d.padStart(2, '0') : '';
    return y && mm && dd ? `${y}-${mm}-${dd}` : `${y}-${mm}-${dd}`.replace(/-+$/g, '');
  };

  const hasAddress = (addr) => {
    if (!addr) return false;
    const pc = String(addr.postal_code || '').trim();
    const st = String(addr.state || '').trim();
    const city = String(addr.city || '').trim();
    const l1 = String(addr.line1 || '').trim();
    return pc && st && city && l1;
  };

  const getEffectiveProfileSnapshot = () => {
    const name = normalizeName(profileDraft.name) || savedName;
    const gender = String(profileDraft.gender || "").trim() || savedGender;
    const birthday = buildBirthday() || savedBirthday;

    const mergedAddress = {
      postal_code: String(profileDraft.address.postal_code || "").trim() || savedAddress.postal_code,
      state: String(profileDraft.address.state || "").trim() || savedAddress.state,
      city: String(profileDraft.address.city || "").trim() || savedAddress.city,
      line1: String(profileDraft.address.line1 || "").trim() || savedAddress.line1,
      line2: String(profileDraft.address.line2 || "").trim() || savedAddress.line2,
      country: String(profileDraft.address.country || "").trim() || savedAddress.country || "JP",
    };

    const addressOk = hasAddress(mergedAddress);

    return { name, gender, birthday, addressOk, address: mergedAddress };
  };
  

  const isProfileSatisfied = (mode /* 'basic' | 'plan' */) => {
    const s = getEffectiveProfileSnapshot();

    if (!s.name || !s.gender) return false;

    if (mode === 'plan') {
      const email = normalizeEmail(profileDraft.email) || savedEmail;
      if (!email) return false;

      if (!/^\d{4}-\d{2}-\d{2}$/.test(s.birthday)) return false;
      if (!s.addressOk) return false;
    }

    return true;
  };;

  const validateProfile = (mode /* 'basic' | 'plan' */) => {
    const s = getEffectiveProfileSnapshot();

    if (!s.name) return '氏名を入力してください。';
    if (!s.gender) return '性別を選択してください。';

    if (mode === 'plan') {
      const email = normalizeEmail(profileDraft.email) || savedEmail;
      if (!email) {
        return 'メールアドレスを入力してください。';
      }

      if (!/^\S+@\S+\.\S+$/.test(email)) {
        return 'メールアドレスの形式をご確認ください。';
      }

      if (!/^\d{4}-\d{2}-\d{2}$/.test(s.birthday)) {
        return '生年月日は「年・月・日」をすべて入力してください。';
      }

      if (!s.addressOk) {
        return '住所（郵便番号・都道府県・市区町村・町名/番地）を入力してください。';
      }
    }

    if (requireTermsConsent && !agreedToTerms) {
      return '利用規約およびプライバシーポリシーへの同意が必要です。';
    }

    return '';
  };

  const openProfileStep = (action) => {
    setProfileError('');
    setPendingAction(action);
    setView('profile');
    setAgreedToTerms(false);
  };

  // ---------- cart operations ----------
  // ---------- cart operations ----------
  const addToCartInternal = (p) => {
    // ★A+C方針：表示/カート/決済の正は products 側（unit_amount / active_price_id）
    const productId = String(p?.id || '').trim();
    if (!productId) return;

    // ここで最新 price を強制（active_price_id -> default_price -> p.price.id）
    const latestPriceId = resolveLatestPriceIdForProduct(p);
    const fallback = String(p?.price?.id || '').trim();
    const priceId = String(latestPriceId || fallback || '').trim();

    if (!priceId) return;

    // 価格形式チェック（古い/不正を弾く）
    if (!String(priceId).startsWith('price_')) {
      console.warn('[ShopModalLocal] invalid priceId for cart', { productId: p?.id, priceId, p });
      alert('この商品の価格情報が不正です。管理画面で価格設定をご確認ください。');
      return;
    }

    // ★表示価格の正：p.unit_amount（なければ従来互換で p.price.unit_amount）
    const unitAmount =
      Number(p?.unit_amount ?? p?.price?.unit_amount ?? 0) || 0;

    setCart((prev) => {
      const existing = prev[priceId];
      const nextQty = (existing?.qty || 0) + 1;

      return {
        ...prev,
        [priceId]: {
          priceId,
          productId: p.id,
          name: p.name,
          description: p.description,
          unit_amount: unitAmount,
          type: p.type, // ticket | goods
          qty: nextQty,
        },
      };
    });
  };

  const addToCart = (p) => {
    addToCartInternal(p);
  };

  const decCartItem = (priceId) => {
    setCart((prev) => {
      const cur = prev[priceId];
      if (!cur) return prev;
      const nextQty = (cur.qty || 0) - 1;
      if (nextQty <= 0) {
        const { [priceId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [priceId]: { ...cur, qty: nextQty } };
    });
  };

  const incCartItem = (priceId) => {
    setCart((prev) => {
      const cur = prev[priceId];
      if (!cur) return prev;
      return { ...prev, [priceId]: { ...cur, qty: (cur.qty || 0) + 1 } };
    });
  };

  const removeCartItem = (priceId) => {
    setCart((prev) => {
      const { [priceId]: _, ...rest } = prev;
      return rest;
    });
  };

  const clearCart = () => setCart({});

  // ---------- checkout ----------
  //callable版
  const execCheckoutPlanInternal = async (priceId) => {
    const user = auth.currentUser;
    if (!user) {
      console.warn('[ShopModalLocal] No auth user');
      return;
    }

    setPurchasing(priceId);

    try {
      const fn = httpsCallable(functions, "createCheckout");

      const res = await fn({
        tenantId: TENANT_ID,
        items: [{ price: String(priceId || "").trim(), quantity: 1 }],
        success_url: `${window.location.origin}?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: window.location.origin,
        billing_address_collection: "auto",
      });

      const data = res?.data || {};

      if (!data?.url) {
        console.error("[ShopModalLocal] NO_CHECKOUT_URL", data);
        alert("チェックアウトURLの取得に失敗しました。");
        return;
      }

      clearPublicShopCart();
      window.location.assign(data.url);

      } catch (err) {

        const code =
          err?.code ||
          err?.data?.code ||
          null;

        const message =
          err?.message ||
          err?.details ||
          err?.data?.message ||
          "";

        const isFailedPrecondition =
          typeof code === "string" &&
          code.includes("failed-precondition");

        // ===============================
        // ✅ 想定内ブロック（ビジネスルール）
        // ===============================
        if (
          isFailedPrecondition ||
          message.includes("請求が確定するまでは変更できません")
        ) {
          console.warn("[ShopModalLocal] plan change blocked (expected)");
          alert(message);
          return;
        }

        // ===============================
        // ❗ 本当のエラーのみ error
        // ===============================
        console.error("[ShopModalLocal] change plan error", {
          code,
          message,
          raw: err,
        });

        alert(message || "プラン変更に失敗しました。");

      } finally {
        setChangingPlanPriceId(null);
        setPurchasing(null);
      }
    };

  // callable: changeSubscriptionPlan（※これを唯一の定義にする）
    const execChangeSubscriptionPlan = async (priceId) => {
      if (publicView || !auth.currentUser) {
        goToAuthForShop();
        return;
      }
    const user = auth.currentUser;
    if (!user) {
      console.warn('[ShopModalLocal] No auth user');
      alert('ログインが必要です。');
      return;
    }

    const targetPriceId = String(priceId || '').trim();
    if (!targetPriceId || !targetPriceId.startsWith('price_')) {
      console.warn('[ShopModalLocal] invalid targetPriceId', { targetPriceId, priceId });
      alert('プラン価格IDが不正です。管理画面の価格設定をご確認ください。');
      return;
    }

    // ✅ 月額契約/変更は「氏名＋メール＋住所＋生年月日」必須（契約と同じ条件）
    if (!isProfileSatisfied('plan')) {
      openProfileStep({ type: 'CHANGE_PLAN', priceId: targetPriceId });
      return;
    }

    setChangingPlanPriceId(targetPriceId);
    setPurchasing(targetPriceId);

    try {
      const fn = httpsCallable(functions, 'changeSubscriptionPlan');

      const res = await fn({
        tenantId: TENANT_ID,
        targetPriceId,
      });

      const data = res?.data || {};

      // ✅ ① サーバーが return blocked 方式の場合
      if (data?.blocked) {
        console.warn('[ShopModalLocal] plan change blocked (expected)');
        alert(data?.message || '現在の請求が確定するまで変更できません。');
        return;
      }

      // ✅ ② サーバーが ok=false を返す場合
      if (data?.ok === false) {
        alert(data?.message || 'プラン変更に失敗しました。');
        return;
      }

      // ✅ 本当の成功のみここに来る
      alert('プラン変更を受け付けました。');

    } catch (err) {

      const code = err?.code || '';
      const message =
        err?.message ||
        err?.details ||
        err?.data?.message ||
        'プラン変更に失敗しました。';

      const isBusinessBlock =
        typeof code === 'string' &&
        code.includes('failed-precondition');

      if (isBusinessBlock) {
        console.warn('[ShopModalLocal] plan change blocked (expected)');
        alert(message);
        return;
      }

      console.error('[ShopModalLocal] change plan error', err);
      alert(message);

    } finally {
      setChangingPlanPriceId(null);
      setPurchasing(null);
    }  
  
  };

  const beginCheckoutCartFlow = useCallback(() => {
    if (publicView || !auth.currentUser) {
      goToAuthForShop({
        type: "CHECKOUT_CART",
      });
      return;
    }

    if (!isProfileSatisfied("basic")) {
      openProfileStep({ type: "CHECKOUT_CART" });
      return;
    }

    execCheckoutCart();
  }, [publicView, cartItems, profileDraft, userProfile, agreedToTerms, requireTermsConsent]);

  const beginCheckoutPlanFlow = useCallback((priceId) => {
    const normalizedPriceId = String(priceId || "").trim();

    if (publicView || !auth.currentUser) {
      goToAuthForShop({
        type: "CHECKOUT_PLAN",
        priceId: normalizedPriceId,
      });
      return;
    }

    if (!isProfileSatisfied("plan")) {
      openProfileStep({
        type: "CHECKOUT_PLAN",
        priceId: normalizedPriceId,
      });
      return;
    }

    execCheckoutPlanInternal(normalizedPriceId);
  }, [publicView, profileDraft, userProfile, agreedToTerms, requireTermsConsent]);

  const execCheckoutCart = async () => {
    if (publicView || !auth.currentUser) {
      goToAuthForShop({
        type: "CHECKOUT_CART",
      });
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      console.warn('[ShopModalLocal] No auth user');
      return;
    }
  if (cartItems.length === 0) return;

  // ✅ カート決済も「氏名＋メール」必須
  if (!isProfileSatisfied('basic')) {
    openProfileStep({ type: 'CHECKOUT_CART' });
    return;
  }

  setPurchasing('__cart__');

  try {
    // ✅ 決済直前に「最新 priceId」へ正規化して items を作る（state更新待ちしない）
    const normalizedItems = cartItems
      .map((it) => {
        const productId = String(it?.productId || '').trim();
        const latest = productId ? String(latestPriceByProductId.get(productId) || '').trim() : '';
        const price = String(latest || it?.priceId || '').trim();
        const quantity = Math.max(1, Number(it?.qty ?? it?.quantity ?? 1) || 1);
        return price ? { price, quantity } : null;
      })
      .filter(Boolean);

    // ここで priceId の妥当性チェック（古いのが混ざってたら止める）
    const bad = normalizedItems.find((x) => !String(x.price).startsWith('price_'));
    if (bad) {
      console.warn('[ShopModalLocal] invalid priceId in cart(normalized)', bad, normalizedItems);
      alert('カート内に価格情報が不正な商品があります。いったんカートを空にして入れ直してください。');
      setPurchasing(null);
      return;
    }

    const fn = httpsCallable(functions, "createCheckout");

    const res = await fn({
      tenantId: TENANT_ID,
      items: normalizedItems,
      success_url: `${window.location.origin}?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: window.location.origin,
    });

    const data = res?.data || {};
    if (!data?.url) throw new Error("NO_CHECKOUT_URL");

    clearPublicShopCart();
    window.location.assign(data.url);

    } catch (err) {
      console.error('[ShopModalLocal] Cart Checkout Error:', {
        code: err?.code,
        message: err?.message,
        details: err?.details,
        raw: err,
      });

      const code = String(err?.code || "");
      const details = err?.details || {};
      const message =
        err?.message ||
        "決済の開始に失敗しました。";

      const isProfileBlocked =
        code.includes("failed-precondition") &&
        (details?.reason === "gender_restricted" ||
          details?.reason === "attribute_restricted");

      if (isProfileBlocked) {
        const profileIncomplete = !isProfileSatisfied("basic");

        // まずプロフィール不足なら、削除アラートより先にプロフィール確認へ
        if (profileIncomplete) {
          setPurchasing(null);
          openProfileStep({ type: "CHECKOUT_CART" });
          return;
        }

        // プロフィールが十分あるのに対象外なら、その時だけ削除する
        const blockedPriceId = String(details?.priceId || "").trim();

        if (blockedPriceId) {
          setCart((prev) => {
            const next = { ...(prev || {}) };
            delete next[blockedPriceId];
            return next;
          });
        }

        alert(`${message}\n商品をカートから削除しました。あらためてカートに入れ直してください。`);
        setPurchasing(null);
        return;
      }

      alert(String(message));
      setPurchasing(null);
}

};

  const execCheckoutPlan = async (priceId) => {
    if (publicView || !auth.currentUser) {
      goToAuthForShop({
        type: "CHECKOUT_PLAN",
        priceId: String(priceId || "").trim(),
      });
      return;
    }

    if (!isProfileSatisfied('plan')) {
      openProfileStep({ type: 'CHECKOUT_PLAN', priceId });
      return;
    }

    await execCheckoutPlanInternal(priceId);
  };
  // ---------- profile save ----------

  //const profileMode = pendingAction?.type === 'CHECKOUT_PLAN' ? 'plan' : 'basic';
  const profileMode =
    (pendingAction?.type === 'CHECKOUT_PLAN' || pendingAction?.type === 'CHANGE_PLAN')
      ? 'plan'
      : 'basic';

  const savedName = String(
    userProfile?.name ||
    userProfile?.metadata?.name ||
    ""
  ).trim();

  const savedEmail = String(
    userProfile?.email ||
    auth.currentUser?.email ||
    ""
  ).trim();

  const savedGender = String(
    userProfile?.gender ||
    userProfile?.metadata?.gender ||
    ""
  ).trim();

  const savedBirthday = String(
    userProfile?.metadata?.birthday ||
    ""
  ).trim();

  const savedAddress = {
    postal_code: String(userProfile?.address?.postal_code || "").trim(),
    state: String(userProfile?.address?.state || "").trim(),
    city: String(userProfile?.address?.city || "").trim(),
    line1: String(userProfile?.address?.line1 || "").trim(),
    line2: String(userProfile?.address?.line2 || "").trim(),
    country: String(userProfile?.address?.country || "JP").trim() || "JP",
  };

  const savedAddressOk = hasAddress(savedAddress);

  // 表示制御は保存済みデータだけで判断
  const needsName = !savedName;
  const needsEmail = profileMode === "plan" && !savedEmail;
  const needsGender = !savedGender;
  const needsBirthday = profileMode === "plan" && !savedBirthday;
  const needsAddress = profileMode === "plan" && !savedAddressOk;

  const hasAnyMissingField =
    needsName || needsEmail || needsGender || needsBirthday || needsAddress;


const resumedIntentRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    if (publicView) return;
    if (!auth.currentUser) return;
    if (!profileSourceReady) return;
    if (!profileDraftReady) return;
    if (resumedIntentRef.current) return;

    const intent = loadCheckoutIntent();
    if (!intent?.type) return;

    resumedIntentRef.current = true;
    clearCheckoutIntent();

    setTimeout(() => {
      if (intent.type === "CHECKOUT_CART") {
        beginCheckoutCartFlow();
        return;
      }

      if (intent.type === "CHECKOUT_PLAN" && intent.priceId) {
        beginCheckoutPlanFlow(intent.priceId);
      }
    }, 0);
  }, [
    isOpen,
    publicView,
    profileSourceReady,
    profileDraftReady,
    beginCheckoutCartFlow,
    beginCheckoutPlanFlow,
  ]);

  const saveProfileAndContinue = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const err = validateProfile(profileMode);
    if (err) {
      setProfileError(err);
      return;
    }

    setProfileError('');
    setPurchasing('__profile__');

    try {
      const authUid = auth.currentUser?.uid;
      const customerUid =
        String(userProfile?.customerUid || userProfile?.customer_uid || "").trim() || authUid;

      const customerRef = tDoc('customers', customerUid, TENANT_ID);
      const birthday = buildBirthday();
      const effective = getEffectiveProfileSnapshot();

      const effectiveEmail = normalizeEmail(profileDraft.email) || savedEmail;

      const payload = {
        name: effective.name,
        email: effectiveEmail || null,
        address: effective.address,
        metadata: {
          name: effective.name,
          gender: effective.gender,
          birthday: effective.birthday,
        },
        ...(requireTermsConsent
          ? {
              consents: {
                terms_url: String(termsUrl || '').trim(),
                terms_agreed_at: new Date().toISOString(),
              },
            }
          : {}),
      };

      await setDoc(customerRef, payload, { merge: true });

      const action = pendingAction;
      setPurchasing(null);
      setPendingAction(null);
      setView('shop');

      if (!action) return;

      if (action.type === 'ADD_TO_CART' && action.product) {
        addToCartInternal(action.product);
        return;
      }

      if (action.type === 'CHECKOUT_PLAN' && action.priceId) {
        await execCheckoutPlanInternal(action.priceId);
        return;
      }
           // if (action.type === 'CHANGE_PLAN' && action.priceId) {
           if (action.type === 'CHANGE_PLAN' && action.priceId) {
        await execChangeSubscriptionPlan(action.priceId);
        return;
      }

      if (action.type === 'CHECKOUT_CART') {
        await execCheckoutCart();
      }
    } catch (e) {
      console.error('[ShopModalLocal] saveProfile error', e);
      setPurchasing(null);
      setProfileError('保存に失敗しました。通信状況をご確認の上、もう一度お試しください。');
    }
  };

  // ----- Birth auto-advance handlers -----
  const clampDigits = (v) => String(v || '').replace(/[^\d]/g, '');

  const onBirthYChange = (v) => {
    const digits = clampDigits(v).slice(0, 4);
    setProfileDraft((p) => ({ ...p, birthY: digits }));
    if (digits.length === 4) {
      requestAnimationFrame(() => mRef.current?.focus());
    }
  };

  const onBirthMChange = (v) => {
    let digits = clampDigits(v).slice(0, 2);
    if (digits.length === 2) {
      const n = Math.max(1, Math.min(12, parseInt(digits, 10)));
      digits = String(n).padStart(2, '0');
    }
    setProfileDraft((p) => ({ ...p, birthM: digits }));
    if (digits.length === 2) {
      requestAnimationFrame(() => dRef.current?.focus());
    }
  };

  const onBirthDChange = (v) => {
    let digits = clampDigits(v).slice(0, 2);
    if (digits.length === 2) {
      const n = Math.max(1, Math.min(31, parseInt(digits, 10)));
      digits = String(n).padStart(2, '0');
    }
    setProfileDraft((p) => ({ ...p, birthD: digits }));
  };

  const backFromProfile = () => {
    setProfileError('');
    setPendingAction(null);
    setView('shop');
  };

  if (!isOpen) return null;

  const showPreparingHint = !preloadedReady;

  return (
    <div className="fixed inset-0 bg-black/60 z-[99999] flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-md animate-fade-in text-gray-900">
      <div
  className="bg-white w-full max-w-md rounded-3xl shadow-2xl relative flex flex-col overflow-hidden font-sans"
        style={{
          // 画面から safe-area と外側余白を引いた分だけを上限にする
          maxHeight: "calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 32px)",
          width: "100%",
        }}
      >
        {/* Header */}
        {/* ストアではタブの仕切り線があるので見出しの下線は引かない */}
        <div className={`px-6 py-3 ${view === 'profile' ? 'border-b border-gray-200' : ''} flex justify-between items-center flex-none bg-white/95 backdrop-blur-sm`}>
          <div className="flex items-center gap-2">
            {view === 'profile' ? (
              <button
                type="button"
                onClick={backFromProfile}
                className="px-3 py-2 bg-gray-900 text-white rounded-3xl font-black text-[12px] flex items-center gap-2 active:scale-[0.99] transition-all"
                aria-label="back"
              >
                <ArrowLeft size={18} />
                戻る
              </button>
            ) : (
              <div className="relative">
                <ShoppingCart size={20} className="text-gray-900" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-gray-900 text-white text-[10px] font-black flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </div>
            )}

            <h3 className="font-black text-base text-gray-800 tracking-tight">
              {view === 'profile' ? 'プロフィール確認' : 'STORE'}
            </h3>
          </div>

          {/* ✅ プロフィール編集 view では閉じるボタン不要 */}
          {view === 'shop' && (
            <button
              type="button"
              onClick={() => {
                setPurchasing(null);
                onClose?.();
              }}
              className="p-2 bg-gray-100 rounded-full text-gray-500 hover:text-gray-600 transition-colors"
              aria-label="close"
            >
              <X size={18} />
            </button>
          )}
          {view === 'profile' && <div className="w-10" />}
        </div>

        {/* ===== VIEW: PROFILE STEP ===== */}
        {view === 'profile' ? (
          <div className="flex flex-col h-full overflow-hidden">
            <div className="flex-1 overflow-y-auto p-5 space-y-4 pb-28">
              <div className="bg-gray-50 border border-gray-100 rounded-3xl p-4">
                <p className="text-[12px] font-black text-gray-800 mb-1">
                  {profileMode === 'plan'
                    ? 'お手続きに必要な情報をご確認ください'
                    : 'ご購入に必要な情報を入力してください'}
                </p>
                  <p className="text-[11px] text-gray-500 font-medium leading-relaxed">
                    {profileMode === 'plan'
                      ? '月額プランのご利用開始に必要な情報をご入力ください。'
                      : '不足している項目をご入力ください。'}
                  </p> 
                </div>

              {/* 氏名 */}
              {needsName && (
              <div className="space-y-1">
                <label className="text-[11px] font-black text-gray-600">氏名（必須）</label>
                <input
                  value={profileDraft.name}
                  onChange={(e) => setProfileDraft((p) => ({ ...p, name: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 font-bold text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="例：田中 美咲"
                  autoComplete="name"
                />
              </div>
              )}

              {needsGender && (
              <div className="space-y-2">
                <label className="text-[11px] font-black text-gray-600">性別（必須）</label>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setProfileDraft((p) => ({ ...p, gender: '男性' }))
                    }
                    className={`rounded-3xl border-2 py-3 font-black text-[13px] transition-all ${
                      profileDraft.gender === '男性'
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 bg-white text-gray-700'
                    }`}
                  >
                    男性
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setProfileDraft((p) => ({ ...p, gender: '女性' }))
                    }
                    className={`rounded-3xl border-2 py-3 font-black text-[13px] transition-all ${
                      profileDraft.gender === '女性'
                        ? 'border-gray-900 bg-gray-900 text-white'
                        : 'border-gray-200 bg-white text-gray-700'
                    }`}
                  >
                    女性
                  </button>
                </div>
              </div>
              )}

              {/* PLANのみ：メール + 生年月日 + 住所 */}
              {profileMode === 'plan' && (
                <>
                  {needsEmail && (
                    <div className="space-y-1">
                      <label className="text-[11px] font-black text-gray-600">
                        メールアドレス（必須）
                      </label>
                      <input
                        type="email"
                        value={profileDraft.email}
                        onChange={(e) =>
                          setProfileDraft((p) => ({ ...p, email: e.target.value }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 font-bold text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-200"
                        placeholder="example@gmail.com"
                        autoComplete="email"
                        inputMode="email"
                      />
                      <p className="text-[10px] text-gray-500 font-medium leading-relaxed">
                        継続利用時の確認や、端末変更時のご本人確認に使用します。
                      </p>
                    </div>
                  )}

                  {/* 生年月日 */}
                  {needsBirthday && (
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-gray-600">生年月日（必須）</label>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <div className="text-[10px] font-black text-gray-500">年</div>
                        <input
                          ref={yRef}
                          value={profileDraft.birthY}
                          onChange={(e) => onBirthYChange(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 font-black text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-200"
                          placeholder="1969"
                          inputMode="numeric"
                          autoComplete="bday-year"
                        />
                      </div>

                      <div className="space-y-1">
                        <div className="text-[10px] font-black text-gray-500">月</div>
                        <input
                          ref={mRef}
                          value={profileDraft.birthM}
                          onChange={(e) => onBirthMChange(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 font-black text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-200"
                          placeholder="08"
                          inputMode="numeric"
                          autoComplete="bday-month"
                        />
                      </div>

                      <div className="space-y-1">
                        <div className="text-[10px] font-black text-gray-500">日</div>
                        <input
                          ref={dRef}
                          value={profileDraft.birthD}
                          onChange={(e) => onBirthDChange(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 font-black text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-200"
                          placeholder="16"
                          inputMode="numeric"
                          autoComplete="bday-day"
                        />
                      </div>
                    </div>

                    <p className="text-[10px] text-gray-500 font-medium">年→月→日 の順に入力すると自動で進みます。</p>
                  </div>
                  )}
                  

                  {/* 住所 */}
                  {needsAddress && (
                  <div className="bg-white rounded-3xl border border-gray-100 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-black text-gray-800">住所（必須）</span>
                      <span className="text-[10px] font-bold text-gray-500">JP</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-500">郵便番号</label>
                        <input
                          value={profileDraft.address.postal_code}
                          onChange={(e) =>
                            setProfileDraft((p) => ({
                              ...p,
                              address: { ...p.address, postal_code: e.target.value },
                            }))
                          }
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 font-bold text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-200"
                          placeholder="6900003"
                          inputMode="numeric"
                          autoComplete="postal-code"
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-500">都道府県</label>
                        <input
                          value={profileDraft.address.state}
                          onChange={(e) =>
                            setProfileDraft((p) => ({
                              ...p,
                              address: { ...p.address, state: e.target.value },
                            }))
                          }
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 font-bold text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-200"
                          placeholder="島根県"
                          autoComplete="address-level1"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-gray-500">市区町村</label>
                      <input
                        value={profileDraft.address.city}
                        onChange={(e) =>
                          setProfileDraft((p) => ({
                            ...p,
                            address: { ...p.address, city: e.target.value },
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 font-bold text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-200"
                        placeholder="松江市"
                        autoComplete="address-level2"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-gray-500">町名・番地</label>
                      <input
                        value={profileDraft.address.line1}
                        onChange={(e) =>
                          setProfileDraft((p) => ({
                            ...p,
                            address: { ...p.address, line1: e.target.value },
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 font-bold text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-200"
                        placeholder="乃白 1-2-3"
                        autoComplete="address-line1"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-gray-500">建物名・部屋番号（任意）</label>
                      <input
                        value={profileDraft.address.line2}
                        onChange={(e) =>
                          setProfileDraft((p) => ({
                            ...p,
                            address: { ...p.address, line2: e.target.value },
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 font-bold text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-200"
                        placeholder="2029"
                        autoComplete="address-line2"
                      />
                    </div>
                  </div>
                )}
                </>
              )}
            

              {/* ✅ 利用規約・プライバシーポリシー同意（terms_urlがある時のみ表示＆必須） */}
              {requireTermsConsent && (
                <div className="bg-white rounded-3xl border border-gray-100 p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <input
                      id="termsAgree"
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-gray-900"
                    />
                    <label htmlFor="termsAgree" className="text-[11px] font-bold text-gray-700 leading-relaxed">
                      利用規約およびプライバシーポリシーに同意します
                      <span className="text-[10px] font-black text-gray-500 ml-2">（必須）</span>
                    </label>
                  </div>

                  <a
                    href={termsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-black text-gray-900 hover:text-gray-900"
                  >
                    利用規約・プライバシーポリシーを確認する
                  </a>
                </div>
              )}

              {profileError && (
                <div className="rounded-3xl bg-red-50 border border-red-100 px-4 py-3 text-[11px] font-bold text-red-600">
                  {profileError}
                </div>
              )}
            </div>

            {/* 固定フッター */}
            <div className="absolute bottom-0 left-0 right-0 bg-white border-t shadow-[0_-10px_30px_rgba(0,0,0,0.06)] p-4">
              <button
                type="button"
                onClick={saveProfileAndContinue}
                disabled={purchasing !== null || (requireTermsConsent && !agreedToTerms)}
                className="w-full py-3 rounded-3xl bg-gray-900 text-white font-black text-[12px] shadow-lg active:scale-[0.99] transition-all disabled:opacity-40"
              >
                {purchasing === '__profile__' ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader size={16} className="text-white" />
                    保存中...
                  </div>
                ) : pendingAction?.type === 'CHECKOUT_PLAN' ? (
                  '保存して契約へ進む'
                ) : (
                  '保存して決済へ進む'
                )}
              </button>

              <div className="mt-2 text-[10px] text-gray-500 font-medium leading-relaxed">
                保存後は元の操作に自動で戻ります（カート追加／契約の続行）。
              </div>
            </div>
          </div>
        ) : (
          /* ===== VIEW: SHOP ===== */
          <div className="flex flex-col h-full overflow-hidden">
            {/* ファイルのタブ(AKUTOブランド基準)。選んでいるタブは下の一覧(gray-50)とつながる。
                お客様画面なので縁はグレー(スチールブルー=お店の色は「増やす」だけに使う) */}
            <div className="flex items-end border-b border-accent bg-white pt-2 px-4 gap-1 flex-none">
              {Object.entries(TAB_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  aria-current={activeTab === key ? 'page' : undefined}
                  className={`relative -mb-px flex-1 rounded-t-xl border border-b-0 transition-colors ${
                    activeTab === key
                      ? 'bg-gray-50 text-gray-900 border-accent'
                      : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-accent/50'
                  }`}
                >
                  {activeTab === key && (
                    <>
                      {/* 根元の曲線。縦の縁→曲線→下の横線を1本につなぐ */}
                      <span aria-hidden className="pointer-events-none absolute bottom-0 -left-[13px] h-3 w-[13px]" style={{ background: 'radial-gradient(circle at 0 0, transparent 11.5px, rgb(var(--tenant-accent)) 11.5px, rgb(var(--tenant-accent)) 12.5px, #F9FAFB 12.5px)' }} />
                      <span aria-hidden className="pointer-events-none absolute bottom-0 -right-[13px] h-3 w-[13px]" style={{ background: 'radial-gradient(circle at 100% 0, transparent 11.5px, rgb(var(--tenant-accent)) 11.5px, rgb(var(--tenant-accent)) 12.5px, #F9FAFB 12.5px)' }} />
                    </>
                  )}
                  <div className="py-3 flex flex-col items-center justify-center gap-1.5">
                    <config.icon size={18} />
                    <div className="flex items-center gap-1">
                      <span className="text-[12px] font-black leading-none">{config.label}</span>

                      {/* ✅ plan だけ pending_change バッジ */}
                      {key === 'plan' && pendingChangeActive && (
                        <span className="ml-1 px-2 py-[2px] rounded-full bg-gray-900/10 text-gray-900 text-[9px] font-black leading-none">
                          予約中
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

<div
  ref={listRef}
  className="flex-1 min-h-0 overflow-y-auto bg-gray-50 p-4 space-y-4 pb-[320px] scrollbar-hide text-gray-900"
>
  {tabDescriptionText && (
    <div
      className={`rounded-3xl border px-4 py-3 text-[11px] font-bold leading-relaxed ${tabDescStyle.box} ${tabDescStyle.text}`}
    >
      <div className="flex items-start gap-2">
        {/* i（丸囲み） */}
        <span
          className={`flex-none w-4 h-4 rounded-full border-2 flex items-center justify-center text-[10px] font-black leading-none mt-[1px] ${tabDescStyle.icon}`}
        >
          i
        </span>

        {/* 本文：2行目以降もこの開始位置に揃う */}
        <div className="flex-1 min-w-0 whitespace-pre-line">
          {tabDescriptionText}
        </div>
      </div>
    </div>
  )}

    {activeTab === "plan" && (
      <ShopModalMessage
        pendingActive={pendingChangeActive}
        effectiveAtMs={pendingChange?.effectiveAtMs || null}
        targetPlanName={pendingTargetPlanName}
      />
    )}

          {showPreparingHint && (
                <div className="rounded-3xl bg-gray-50 border border-gray-100 px-4 py-3 text-[11px] font-bold text-gray-500">
                  商品情報を準備しています...
                </div>
              )}

              {products.length > 0 ? (
                products.map((p) => {
                // ★A+C方針：表示/カート/決済の正は products 側（unit_amount / active_price_id）
                //const priceId = String(resolveLatestPriceIdForProduct(p) || '').trim();
                const priceId = String(
                  resolveStripePriceId(p) ||
                  resolveLatestPriceIdForProduct(p) ||
                  ''
                ).trim();

                // inCartQty は「priceIdで引く」だと priceId が変わった瞬間に 0 になり得るため、
                // productId ベースで合算する（体感のズレを防ぐ）
                const inCartQty = Object.values(cart || {}).reduce((sum, it) => {
                  return sum + (String(it?.productId || '') === String(p?.id || '') ? (Number(it?.qty || 0) || 0) : 0);
                }, 0);

                // ★表示価格の正：p.unit_amount（なければ従来互換で p.price.unit_amount）
                const unitAmount = Number(p?.unit_amount ?? p?.price?.unit_amount ?? 0) || 0;

                return (
                  <div
                    key={p.id}
                    className="bg-white p-5 rounded-3xl shadow-[0_4px_12px_rgba(0,0,0,0.04)] border border-gray-200 flex justify-between items-center gap-4 group hover:border-gray-200 transition-all"
                  >
                    <div className="flex-1 min-w-0 text-gray-900">
                      <ProductTitle name={p.name} />
                      <p className="text-[10px] text-gray-500 font-medium line-clamp-2 leading-relaxed">
                        {p.description}
                      </p>
                    </div>

                    <div className="text-right flex flex-col items-end gap-2 flex-none text-gray-900">
                      <span className="text-base font-black tabular-nums">
                        ¥{unitAmount.toLocaleString()}
                      </span>

                      {activeTab === 'plan' ? (() => {
                        // ✅ 既存会員判定（どちらか入っていれば会員扱い）
                        // ✅ 既存会員判定は plan_active 由来に統一
                        const isPlanMember = Boolean(hasActivePlan);

                        // ✅ 現在の priceId（plan_active 由来）
                        const curPriceId = isPlanMember ? String(currentPlanPriceId || '').trim() : '';

                        // ✅ いま表示しているプランが現在プランと同一か
                        const targetPriceId = String(priceId || '').trim();
                        const isSamePlan = curPriceId && targetPriceId && targetPriceId === curPriceId;

                        const onClickPlan = () => {
                          if (isPlanMember) {
                            if (isSamePlan) {
                              alert('すでにこのプランに加入しています。');
                              return;
                            }
                            execChangeSubscriptionPlan(targetPriceId);
                          } else {
                            beginCheckoutPlanFlow(targetPriceId);
                          }
                        };

                        return (
                        <button
                          type="button"
                          onClick={onClickPlan}
                          disabled={
                            purchasing !== null ||
                            !targetPriceId ||
                            isSamePlan ||
                            changingPlanPriceId === targetPriceId
                          }
                          className={`px-6 py-2 ${TAB_CONFIG[activeTab].btnBg} text-white font-black rounded-xl text-[11px] shadow-md active:scale-95 transition-all disabled:opacity-50`}
                        >
                          {purchasing === targetPriceId || changingPlanPriceId === targetPriceId ? (
                            <Loader size={14} className="text-white" />
                          ) : isPlanMember ? (
                            isSamePlan ? '加入中' : 'プラン変更'
                          ) : (
                            '契約する'
                          )}
                        </button>                        );                      })() : (
                        <div className="flex flex-col items-end gap-2">
                          <button
                            type="button"
                            onClick={() => addToCart(p)}
                            disabled={purchasing !== null || !priceId}
                          className={`w-[120px] px-5 py-2 font-black rounded-xl text-[11px] transition-all duration-200 disabled:opacity-50 ${
                            inCartQty > 0
                              ? 'bg-gray-100 text-gray-800 border border-gray-400 tracking-wide'
                              : `${TAB_CONFIG[activeTab].btnBg} text-white shadow-md active:scale-95`
                          }`}
                          >
                            {inCartQty > 0
                              ? `追加済み（${inCartQty}）`
                              : 'カートに入れる'}
                          </button>

                        </div>
                      )}
                     </div>
                  </div>
                );
              })
              ) : (
                <div className="text-center py-20 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                  <Info size={24} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-[11px] text-gray-500 font-black uppercase tracking-widest">商品がありません</p>
                </div>
              )}
            </div>

            {/* Cart Footer (goods/ticket のみ) */}
            {isCartTab && (
              <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-300 shadow-[0_-25px_50px_rgba(0,0,0,0.12)] p-4 max-h-[290px] flex flex-col">
                {/* 上段：アイコン+カート（左） / 小計+金額（右） */}
                <div className="flex items-center justify-between mb-3 flex-none">
                  <div className="flex items-center gap-2 min-w-0">
                    <ShoppingCart size={18} className="text-gray-700" />
                    <span className="font-black text-sm text-gray-800">カート</span>
                  </div>

                  <div className="flex items-baseline gap-2 flex-none">
                    <span className="text-[11px] font-black text-gray-500">小計</span>
                    <span className="text-base font-black tabular-nums text-gray-900">
                      ¥{cartSubtotal.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* ✅ 内訳：ここだけスクロール。フッター自体は max-h を超えて伸びない */}
                {cartItems.length === 0 ? (
                  <div className="text-[11px] text-gray-500 font-bold mb-3 flex-none">
                    商品を追加してください。
                  </div>
                ) : (
                  <div className="space-y-2 overflow-y-auto pr-1 mb-3 flex-1 min-h-0">
                    {cartItems
                      .slice()
                      .sort((a, b) => (a.type || '').localeCompare(b.type || ''))
                      .map((it) => (
                        <div key={it.priceId} className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-[12px] font-black text-gray-800 whitespace-pre-line break-words">{it.name}</div>
                            <div className="text-[10px] font-bold text-gray-500 tabular-nums">
                              ¥{(it.unit_amount || 0).toLocaleString()} / 点
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-none">
                            <button
                              type="button"
                              onClick={() => decCartItem(it.priceId)}
                              disabled={purchasing !== null}
                              className="w-9 h-9 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center active:scale-95 transition-all disabled:opacity-50"
                              aria-label="decrease"
                            >
                              <Minus size={16} />
                            </button>

                            <span className="w-8 text-center font-black tabular-nums">{it.qty}</span>

                            <button
                              type="button"
                              onClick={() => incCartItem(it.priceId)}
                              disabled={purchasing !== null}
                              className="w-9 h-9 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center active:scale-95 transition-all disabled:opacity-50"
                              aria-label="increase"
                            >
                              <Plus size={16} />
                            </button>

                            <button
                              type="button"
                              onClick={() => removeCartItem(it.priceId)}
                              disabled={purchasing !== null}
                              className="w-9 h-9 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center hover:text-gray-800 active:scale-95 transition-all disabled:opacity-50"
                              aria-label="remove"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {/* 次の行：まとめて購入ボタンのみ（固定） */}
                <button
                  type="button"
                  onClick={beginCheckoutCartFlow}
                  disabled={purchasing !== null || cartItems.length === 0}
                  className="w-full py-3 rounded-3xl bg-gray-900 text-white font-black text-[16px] shadow-lg active:scale-[0.99] transition-all disabled:opacity-40 flex-none"
                >
                  {purchasing === '__cart__' ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader size={16} className="text-white" />
                      決済ページへ移動中...
                    </div>
                  ) : (
                    'ご購入手続きへ'
                  )}
                </button>

                  <div className="text-[11px] text-gray-500 font-bold mb-3 flex-none">
                   
                  </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopModalLocal;
//テナント済み